import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Pagination } from 'grommet';
import { ClientDataTable } from './ClientDataTable';
import { ApiError, ClientService, TListClientsRequest } from '/src/api';
import {
  AddButton,
  Box,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  ClientIcon,
  Filters,
  LoadingSpinner,
  SearchInput,
  Text,
} from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState, useSyncDraft } from '/src/hooks/useRestorableListState';
import { Client } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { TResponseMetadata } from '/src/lib/types';

type ClientListQueryState = {
  page: number;
  search: string;
  is_active: number; // 0=inactive, 1=active, 2=all
  order_by: string | undefined;
  order_direction: string | undefined;
};

const CLIENT_LIST_DEFAULTS: ClientListQueryState = {
  page: 1,
  search: '',
  is_active: 1,
  order_by: undefined,
  order_direction: undefined,
};

const CLIENT_LIST_CLEARED: ClientListQueryState = {
  ...CLIENT_LIST_DEFAULTS,
  is_active: 2,
};

export const ClientList: React.FC<ClientListProps> = (props) => {
  /* Props */
  const {
    title,
    isLoading: propIsLoading,
    clients: propClients,
    fetchClients: propFetchClients,
    currentPage: propCurrentPage,
    setCurrentPage: propSetCurrentPage,
    metadata: propMetadata,
    showFilters,
    showSearchInput,
    showAddButton,
    hideHeader,
    surfaceId,
    standalone = false,
    mode = 'query',
    ...boxProps
  } = props;

  const isPropControlled = mode === 'controlled';

  /* Context */
  const userStore = useUserStore();
  const { handleApiError } = useGlobalStore();
  const { t } = useTranslation();

  /* Restorable state */
  const restorableList = useRestorableListState<ClientListQueryState>({
    surfaceId: surfaceId || 'client-list',
    defaults: CLIENT_LIST_DEFAULTS,
    standalone,
  });

  const queryState = restorableList.state;
  const setQueryState = restorableList.setState;

  /* Draft state */
  const [draftSearch, setDraftSearch] = useState(queryState.search);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [draftFilters, setDraftFilters] = useState<Record<string, any>>({
    is_active: queryState.is_active,
  });

  /* Build the API request */
  const request: TListClientsRequest = useMemo(
    () => ({
      page: queryState.page,
      name: queryState.search || undefined,
      is_active: queryState.is_active === 1 ? true : queryState.is_active === 0 ? false : undefined,
      order_by: queryState.order_by as TListClientsRequest['order_by'],
      order_direction: queryState.order_direction as TListClientsRequest['order_direction'],
    }),
    [queryState]
  );

  /* React Query */
  const {
    data: queryData,
    isLoading: queryIsLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: ['clients', userStore.user?.id, request],
    queryFn: () => ClientService.list(request),
    enabled: !isPropControlled && !!userStore.user,
    placeholderData: (prev) => prev,
  });

  const queryClients = queryData?.data;
  const queryMetadata = queryData?.meta;

  /* Show toast on query error */
  useEffect(() => {
    if (queryError && !isPropControlled) {
      handleApiError(queryError as ApiError, toastMessages.listClients.error);
    }
  }, [queryError]);

  /* Resolved values */
  const _clients = propClients ?? queryClients;
  const _metadata = propMetadata ?? queryMetadata;
  const _currentPage = propCurrentPage ?? queryState.page;
  const _isLoading = propIsLoading ?? (isPropControlled ? false : queryIsLoading || isFetching);
  const isRefetching = isFetching && !queryIsLoading;
  const hasClients = _clients;

  /* Methods */
  const search = useCallback(
    (query?: string) => {
      setQueryState({ search: query || '', page: 1 });
    },
    [setQueryState]
  );

  const applyFilters = useCallback(() => {
    setQueryState({
      is_active: draftFilters.is_active,
      page: 1,
    });
  }, [setQueryState, draftFilters]);

  const clearFilters = useCallback(() => {
    setDraftFilters({ is_active: CLIENT_LIST_CLEARED.is_active });
    setDraftSearch('');
    setQueryState(CLIENT_LIST_CLEARED);
  }, [setQueryState]);

  const handleSort = useCallback(
    (property: string, direction: 'asc' | 'desc') => {
      setQueryState({ order_by: property, order_direction: direction, page: 1 });
    },
    [setQueryState]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (isPropControlled) {
        propSetCurrentPage?.(page);
        propFetchClients?.(page);
      } else {
        setQueryState({ page });
      }
    },
    [isPropControlled, propSetCurrentPage, propFetchClients, setQueryState]
  );

  /* Sync page from API response */
  useEffect(() => {
    if (queryMetadata && !isPropControlled && queryMetadata.current_page !== queryState.page) {
      setQueryState({ page: queryMetadata.current_page });
    }
  }, [queryMetadata?.current_page]);

  /* Sync drafts when queryState changes */
  useSyncDraft(queryState, setDraftSearch, (s) => s.search);
  useSyncDraft(queryState, setDraftFilters, (s) => ({ is_active: s.is_active }));

  /* Render */
  return (
    <Box direction="row" gap="medium" fill="horizontal">
      <Card {...boxProps}>
        {!hideHeader && (
          <CardHeader title={title || t('client:clientList')} icon={<ClientIcon />}>
            {showSearchInput && (
              <SearchInput
                searchQuery={draftSearch}
                setSearchQuery={setDraftSearch}
                onSearch={search}
                isSearching={isRefetching}
              />
            )}
            {showAddButton && userStore.isFuseAdmin && (
              <AddButton label={t('client:addClient')} targetUrl={'/clients/create'} />
            )}
          </CardHeader>
        )}
        <CardBody pad="none" gap="none">
          <Box elevation="small">
            {_isLoading && <LoadingSpinner />}
            {!_isLoading && (
              <ClientDataTable
                clients={_clients}
                onSort={handleSort}
                sortProperty={queryState.order_by as TListClientsRequest['order_by']}
                sortDirection={queryState.order_direction as TListClientsRequest['order_direction']}
              />
            )}
            {!hasClients?.length && !_isLoading && (
              <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
                <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                  No clients found.
                </Text>
              </Box>
            )}
          </Box>
          {!!hasClients?.length && !!_metadata && !!_currentPage && (
            <Box pad="1.5rem" flex="grow">
              <Pagination
                alignSelf="center"
                size="small"
                page={_currentPage}
                step={_metadata?.per_page}
                numberItems={_metadata?.total}
                onChange={(e) => handlePageChange(e.page)}
              />
            </Box>
          )}
        </CardBody>
      </Card>
      {showFilters && (
        <Filters
          isFiltering={isRefetching}
          onSubmit={() => applyFilters()}
          onClear={() => clearFilters()}
          filters={[
            {
              label: t('client:clientStatus'),
              value: draftFilters.is_active,
              setValue: (value) => setDraftFilters({ ...draftFilters, is_active: value }),
              options: [
                { label: t('common:all'), value: 2 },
                { label: t('common:active'), value: 1 },
                { label: t('common:retired'), value: 0 },
              ],
            },
          ]}
        />
      )}
    </Box>
  );
};

export type ClientListProps = BoxProps & {
  title?: string;
  clients?: Client[];
  fetchClients?: (page?: number) => Promise<void>;
  metadata?: TResponseMetadata;
  isLoading?: boolean;
  showAddButton?: boolean;
  showFilters?: boolean;
  showSearchInput?: boolean;
  hideHeader?: boolean;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  surfaceId?: string;
  standalone?: boolean;
  mode?: 'query' | 'controlled';
};
